import { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  type Skill,
  type SkillStat,
  createSkill,
  deleteSkill,
  getSkillStats,
  listSkills,
  patchSkill,
} from "@/api/skills";
import { PageHeader } from "@/components/PageHeader";

export function SkillsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Skill | null>(null);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#1976d2");
  const [isActive, setIsActive] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["skills", "all"],
    queryFn: () => listSkills(false),
  });

  const stats = useQuery({
    queryKey: ["skill-stats"],
    queryFn: () => getSkillStats(),
  });

  const resetForm = () => {
    setCode("");
    setLabel("");
    setColor("#1976d2");
    setIsActive(true);
    setEditTarget(null);
    setFormError(null);
  };

  const startEdit = (s: Skill) => {
    setEditTarget(s);
    setCode(s.code);
    setLabel(s.label);
    setColor(s.color ?? "#1976d2");
    setIsActive(s.is_active);
    setOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editTarget) {
        return patchSkill(editTarget.id, { label, color, is_active: isActive });
      }
      return createSkill({ code, label, color, is_active: isActive });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["skills"] });
      qc.invalidateQueries({ queryKey: ["skill-stats"] });
      setOpen(false);
      resetForm();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteSkill(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills"] }),
  });

  return (
    <Box>
      <PageHeader
        helpTerm="competence"
        title={t("skills.adminTitle")}
        subtitle={t("skills.adminSubtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setOpen(true);
            }}
          >
            {t("skills.create")}
          </Button>
        }
      />

      {stats.data && stats.data.items.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            {t("skills.statsTitle", {
              total: stats.data.items[0]?.org_active_total ?? 0,
            })}
          </Typography>
          <Stack spacing={1.25}>
            {stats.data.items.map((s: SkillStat) => {
              const pct = Math.round(s.coverage_ratio * 100);
              return (
                <Box key={s.skill_id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Chip
                      size="small"
                      label={s.code}
                      sx={{ bgcolor: s.color ?? undefined, color: "#fff", fontFamily: "monospace" }}
                    />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      {s.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {s.practitioner_count} / {s.org_active_total} ({pct}%)
                    </Typography>
                    {s.slot_templates_requiring > 0 && (
                      <Tooltip title={t("skills.slotsRequiringTooltip", { count: s.slot_templates_requiring })}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`req: ${s.slot_templates_requiring}`}
                        />
                      </Tooltip>
                    )}
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    color={pct === 0 ? "error" : pct < 20 ? "warning" : "primary"}
                  />
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {list.isLoading && <CircularProgress />}
      {list.data && list.data.length === 0 && (
        <Alert severity="info">{t("skills.empty")}</Alert>
      )}

      {list.data && list.data.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("skills.code")}</TableCell>
              <TableCell>{t("skills.label")}</TableCell>
              <TableCell>{t("skills.color")}</TableCell>
              <TableCell>{t("skills.active")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {list.data.map((s) => (
              <TableRow key={s.id}>
                <TableCell sx={{ fontFamily: "monospace" }}>{s.code}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={s.label}
                    sx={{ bgcolor: s.color ?? undefined, color: "#fff" }}
                  />
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{s.color ?? "—"}</TableCell>
                <TableCell>
                  {s.is_active ? t("common.yes") : t("common.no")}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title={t("common.edit") ?? "Modifier"}>
                      <IconButton size="small" onClick={() => startEdit(s)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("common.delete") ?? "Supprimer"}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          if (confirm(t("skills.deleteConfirm"))) {
                            deleteMutation.mutate(s.id);
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {editTarget ? t("skills.editTitle") : t("skills.createTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t("skills.code")}
              size="small"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/\s+/g, ""))}
              disabled={Boolean(editTarget)}
              inputProps={{ maxLength: 32 }}
              helperText={t("skills.codeHelp")}
            />
            <TextField
              label={t("skills.label")}
              size="small"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              inputProps={{ maxLength: 120 }}
              autoFocus={!editTarget}
            />
            <TextField
              label={t("skills.color")}
              size="small"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              inputProps={{ maxLength: 16 }}
              placeholder="#1976d2"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span>{t("skills.active")}</span>
            </Stack>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending || !code || !label}
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
